import { IcChevronDown } from '@assets/icons/24x24';
import { IcReplay } from '@assets/icons/32x32';
import { Box, Center, HStack, Icon, VStack } from '@chakra-ui/react';
import { ShowOnMapButton } from '@components/cockpit/panels/notification/card/ShowOnMapButton';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasAnySensorRoleFromActiveProfile, selectActiveUser } from '@redux/authent/authent.selectors';
import { selectNotificationById } from '@redux/data/data.selectors';
import { updateSelectedNotificationId } from '@redux/global/global.reducer';
import { selectSelectedNotificationId } from '@redux/global/global.selector';
import { issueMapCommand } from '@redux/maps/mapsCommands.reducer';
import { updateReplayStartPosition, updateReplayStartTime } from '@redux/replay/replay.reducer';
import { selectReplayUuid } from '@redux/replay/replay.selectors';
import { selectContext } from '@redux/settings/settings.selectors';
import { usePatchContextMutation } from '@services/config/setting.api';
import { useLazyGetDetailQuery } from '@services/data/notification.api';
import { useUpdateStreamMutation } from '@services/replay/replay.api';
import {
  getAuthoritiesForNotificationManagement,
  getNotificationColor,
  getWorstNotificationLevel,
  isDroneNotification,
} from '@utils/data/notification.utils';
import { isEqual } from 'lodash';
import { CSSProperties, Dispatch, memo, useState } from 'react';

import { ContextEnum } from '@/types/config/screenConfiguration.types';
import { AlertType, NotificationType, NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';

import AlertList from './AlertList';
import NotificationCardDates from './NotificationCardDates';
import NotificationCardInfo from './NotificationCardInfo';
import NotificationCardName from './NotificationCardName';
import NotificationCardProcedure from './NotificationCardProcedure';

type Props =
  | {
      notificationId: string;
      style: CSSProperties;
      isSelectable?: boolean;
      isArchived?: false;
      isHistory?: never;
      isSearch?: never;
      archivedNotification?: never;
      isLast?: boolean;
      isSelected?: never;
      setSelectedNotification?: never;
      hideProcedure?: boolean;
    }
  | {
      archivedNotification: NotificationType;
      isSelectable?: boolean;
      notificationId?: never;
      isSelected?: boolean;
      setSelectedNotification?: Dispatch<string | null>;
      style?: CSSProperties;
      isArchived: true;
      isHistory?: boolean;
      isSearch?: boolean;
      isLast?: boolean;
      hideProcedure?: never;
    };

export function NotificationCard({
  notificationId,
  isSelected,
  isSelectable = true,
  hideProcedure = false,
  isArchived = false,
  isHistory = false,
  isSearch = false,
  isLast = false,
  style,
  archivedNotification,
  setSelectedNotification,
}: Readonly<Props>) {
  const selectedNotification = useAppSelector(selectSelectedNotificationId);
  const isNotificationSelected = isSelected || selectedNotification === notificationId;
  const dispatchSetSelected = useWithDispatch(updateSelectedNotificationId);
  const setSelectedNotif = setSelectedNotification ?? dispatchSetSelected;
  const replayUuid = useAppSelector(selectReplayUuid);
  const [updateStream] = useUpdateStreamMutation();
  const setReplayStartTime = useWithDispatch(updateReplayStartTime);
  const setReplayStartPosition = useWithDispatch(updateReplayStartPosition);
  const centerMapView = useWithDispatch(issueMapCommand);
  const currentContext = useAppSelector(selectContext);
  const [patchContext] = usePatchContextMutation();
  const user = useAppSelector(selectActiveUser);

  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [getDetail, { isLoading }] = useLazyGetDetailQuery();

  const [open, setOpen] = useState<boolean>(false);
  const ongoingNotification = useAppSelector((state) => selectNotificationById(state, notificationId), isEqual);

  const notification = isArchived ? archivedNotification : ongoingNotification;
  const canManageNotification = useAppSelector((state) =>
    hasAnySensorRoleFromActiveProfile(state, getAuthoritiesForNotificationManagement(notification)),
  );

  if (!notification) {
    return null;
  }

  const isExpired = notification.endTime !== null && !isArchived;

  const height = isArchived ? 136 : 130;

  const worstAlertLevel = getWorstNotificationLevel(notification, { isArchived: isArchived });

  const replayPosition = isDroneNotification(notification) ? notification.startPosition : notification.position;

  return (
    <VStack
      gap={0}
      height={isHistory ? 'max-content' : `${height}px`}
      minHeight={isHistory ? undefined : `${height}px`}
      width="100%"
      style={style}
      userSelect="none"
      backgroundColor={isNotificationSelected ? 'neutral.700' : 'neutral.800'}
    >
      <HStack
        position="relative"
        gap={0}
        width="100%"
        height={`${height}px`}
        flexShrink={0}
        cursor={isSelectable ? 'pointer' : 'default'}
        onClick={() => isSelectable && setSelectedNotif(isNotificationSelected ? null : notification.id)}
        opacity={isExpired && !isNotificationSelected ? 0.5 : 1}
        borderBottom={isLast ? 'none' : '2px solid'}
        borderColor="neutral.black"
      >
        {isNotificationSelected && (
          <Box position="absolute" width="8px" height="100%" backgroundColor={getNotificationColor(notification)} />
        )}
        <NotificationCardName
          notification={notification}
          isArchived={isArchived}
          isSearch={isSearch}
          isSelected={isNotificationSelected}
        />
        <Box height="75%" width="2px" bg="neutral.900" />
        <NotificationCardInfo notification={notification} isArchived={isArchived} isSearch={isSearch} />
        <Box height="75%" width="2px" bg="neutral.900" />
        <NotificationCardDates
          startTime={notification.startTime}
          endTime={notification.endTime}
          lastDetectionTime={notification.type === NotificationTypeEnum.DRONE ? notification.lastDetectionTime : null}
          lastProcedureFirstAlertStartTime={notification.lastProcedureFirstAlertStartTime}
          lastProcedureClosingTime={notification.lastProcedureClosingTime}
          type={notification.type}
          isArchived={isArchived}
          worstAlertLevel={worstAlertLevel}
          isSearch={isSearch}
        />
        {isArchived ? (
          <>
            <Box height="100%" width="2px" bg="neutral.900" />
            <VStack gap={0} width={isSearch ? '56px' : '64px'} height="100%">
              <Center
                cursor="pointer"
                className="group"
                width="100%"
                flex={1}
                onClick={(e) => {
                  if (currentContext === ContextEnum.REPLAY) {
                    if (replayUuid) {
                      updateStream({
                        uuid: replayUuid,
                        command: { time: new Date(notification.startTime), speed: 0 },
                      });
                    }
                    centerMapView({
                      mapId: MapIdEnum.REPLAY,
                      command: { type: 'CENTER', payload: { center: replayPosition, zoom: 14 } },
                    });
                  } else {
                    setReplayStartTime(notification.startTime);
                    setReplayStartPosition(replayPosition);
                    patchContext({ login: user.login, context: ContextEnum.REPLAY });
                  }
                  e.stopPropagation();
                }}
              >
                <Icon boxSize="32px" as={IcReplay} color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              </Center>
              {!isSearch && (
                <>
                  <Box width="100%" height="2px" bg="neutral.900" />
                  <Center
                    cursor="pointer"
                    className="group"
                    flex={1}
                    width="100%"
                    onClick={(e) => {
                      setOpen(!open);
                      if (!open) {
                        getDetail({ id: notification.id, withArchivedAlerts: true, withDoneProcedures: false })
                          .unwrap()
                          .then(({ detailsByPlatform }) =>
                            setAlerts(Object.values(detailsByPlatform).flatMap((pd) => pd.alerts)),
                          );
                      }
                      e.stopPropagation();
                    }}
                  >
                    <Icon
                      boxSize="24px"
                      as={IcChevronDown}
                      transform={open ? 'rotate(180deg)' : undefined}
                      color={open ? 'neutral.white' : 'neutral.300'}
                      _groupHover={{ color: 'skyBlue' }}
                    />
                  </Center>
                </>
              )}
            </VStack>
          </>
        ) : (
          <>
            {!hideProcedure ? (
              <NotificationCardProcedure
                notificationId={notification.id}
                nbSteps={notification.nbSteps}
                currentStepRank={notification.currentStepRank}
                currentStepType={notification.currentStepType}
                procedureState={notification.procedureState}
                canManageNotification={canManageNotification}
                isMuted={notification.muted}
              />
            ) : (
              <Box height="75%" width="2px" bg="neutral.900" />
            )}
            <ShowOnMapButton notificationLight={notification} isExpired={isExpired} />
          </>
        )}
      </HStack>
      {open && (
        <Box backgroundColor="neutral.900" width="100%">
          <AlertList
            alerts={alerts}
            notificationType={notification.type}
            isLoading={isLoading}
            isArchived={isArchived}
          />
        </Box>
      )}
    </VStack>
  );
}

export default memo(NotificationCard);
